import * as React from 'react';
import { getCatalog, getShouldShowLive } from '@/redux/modules/catalog';
import styled, { css } from 'styled-components';
import BidConsoleOpener from '@/components/BidConsoleOpener/BidConsoleOpener';
import CatalogDate from '@/components/CatalogDate/CatalogDate';
import FormattedCurrency from '@/components/Formatted/FormattedCurrency';
import { FormattedMessage } from 'react-intl';
import { getBiddingInfo } from '@/redux/modules/biddingInfo';
import { getBuyNow } from '@/redux/modules/buyNow';
import { getItem } from '@/redux/modules/item';
import { getLiveCatalogData } from '@/redux/modules/console';
import { getLiveItemStatus } from '@/redux/modules/liveItemStatus';
import { getSeller } from '@/redux/modules/seller';
import { getSellerUrl } from '@/utils/urls';
import isEqual from 'react-fast-compare';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import { safelyRender } from '@/utils/safelyRender';
import Text from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { useSelector } from 'react-redux';

type Props = {
    allowBidding?: boolean;
    hideLiveButton?: boolean;
    hideSeller?: boolean;
    itemId: number;
    showDate?: boolean;
    showEstimate?: boolean;
};

const DetailsRow = (props: Props) => {
    const { allowBidding, hideLiveButton, hideSeller, itemId, showDate, showEstimate } = props;

    const { status: buyNowStatus } = useSelector((state) => getBuyNow(state, itemId), isEqual);
    const { catalogId, highBidEstimate = 0, index, lowBidEstimate = 0, sellerId } = useSelector(
        (state) => getItem(state, itemId),
        isEqual
    );
    const { catalogStatus, currency = 'usd', saleStartTs } = useSelector(
        (state) => getCatalog(state, catalogId),
        isEqual
    );
    const { name: sellerName } = useSelector((state) => getSeller(state, sellerId), isEqual);
    const { open: isLotOpen } = useSelector((state) => getLiveItemStatus(state, itemId), isEqual);
    const { isAvailable, isLocked, isPassed, isSold } = useSelector((state) => getBiddingInfo(state, itemId), isEqual);
    const { currentItemIndex } = useSelector((state) => getLiveCatalogData(state, catalogId), isEqual);
    const safeSellerName = safelyRender(sellerName, 'span');

    const estimateRange = {
        high: <FormattedCurrency noFraction sourceCurrency={currency} value={highBidEstimate} />,
        low: <FormattedCurrency noFraction sourceCurrency={currency} value={lowBidEstimate} />,
    };

    const showLiveButton = useSelector((state) => getShouldShowLive(state, catalogId), isEqual);
    const buyNowAvailable = buyNowStatus === 'available' && catalogStatus === 'done';
    const lotsAway = index - currentItemIndex;
    const isUnsold = isLocked && !isPassed && !isSold && !isLotOpen;
    const comingUp = currentItemIndex <= index;
    const isComingUp = comingUp && isAvailable && !isUnsold;

    return (
        <ContainerDiv>
            {showEstimate && (
                <EstimateLinkContainerDiv caption>
                    <EstimateNameContainerSpan>
                        <FormattedMessage id="short_estimate_values" values={estimateRange} />
                    </EstimateNameContainerSpan>
                </EstimateLinkContainerDiv>
            )}
            {!hideSeller && (
                <AuctioneerLinkContainerDiv caption>
                    <AuctioneerNameContainerSpan>
                        {sellerName ? (
                            <AuctioneerLink primaryWeak route to={getSellerUrl(sellerId, sellerName)}>
                                {safeSellerName}
                            </AuctioneerLink>
                        ) : (
                            <AuctioneerLinkSpan>Jasper52</AuctioneerLinkSpan>
                        )}
                    </AuctioneerNameContainerSpan>
                </AuctioneerLinkContainerDiv>
            )}
            {!hideLiveButton && (
                <CountdownContainerDiv caption>
                    {buyNowAvailable ? null : showLiveButton && isComingUp ? (
                        <ItemLiveSpan>
                            <BidConsoleOpener catalogId={catalogId}>
                                <ItemLiveLinkSpan>
                                    {lotsAway > 0 && allowBidding ? (
                                        lotsAway === 1 && allowBidding ? (
                                            <FormattedMessage id="itemBidding.lotAway_short" />
                                        ) : (
                                            <FormattedMessage id="itemBidding.lotsAway_short" values={{ lotsAway }} />
                                        )
                                    ) : (
                                        <FormattedMessage id="bid_live" />
                                    )}
                                </ItemLiveLinkSpan>
                            </BidConsoleOpener>
                        </ItemLiveSpan>
                    ) : (
                        <CatalogDate
                            date={saleStartTs}
                            forceYear={catalogStatus === 'done' || (catalogStatus === 'live' && !isComingUp)}
                            showDate={showDate}
                        />
                    )}
                </CountdownContainerDiv>
            )}
        </ContainerDiv>
    );
};

export default React.memo<Props>(DetailsRow);

const ContainerDiv = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    @media (max-width: 440px) {
        flex-direction: column;
    }
`;

const LinkContainer = css`
    flex: 1 1 60%;
    display: flex;
    margin: 0 8px 0 0;
    letter-spacing: 0.5px;
    color: ${({ theme }) => theme.colors.grey100};
    max-width: 60%;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin: 0 12px 0 0;
        font-size: 14px;
        line-height: 21px;
    }

    @media (max-width: 440px) {
        max-width: 100%;
    }
`;

const EstimateLinkContainerDiv = styled(Text)`
    ${LinkContainer}
`;

const AuctioneerLinkContainerDiv = styled(Text)`
    ${LinkContainer}
`;

const NameContainer = css`
    flex-grow: 3;
    white-space: nowrap;
    overflow: hidden;
    display: flex;
`;

const EstimateNameContainerSpan = styled.span`
    ${NameContainer}
    overflow: visible;
    > div {
        margin: 0 5px;
    }
`;

const AuctioneerNameContainerSpan = styled.span`
    ${NameContainer}
`;

const AuctioneerLinkStyles = css`
    flex-grow: 3;
    max-width: 100%;
`;

const AuctioneerLink = styled(Link)`
    ${AuctioneerLinkStyles}
`;

const AuctioneerLinkSpan = styled.span`
    ${AuctioneerLinkStyles}
    @include ellipsis();
`;

const CountdownContainerDiv = styled(Text)`
    flex: 0 1 auto;
    display: block;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        float: left;
        font-size: 14px;
        line-height: 21px;
    }
`;

const ItemLiveSpan = styled.span`
    display: inline-block;
`;

const ItemLiveLinkSpan = styled.span`
    color: ${({ theme }) => theme.colors.red100};
    display: inline-block;
    position: relative;
    padding-right: 10px;
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }

    &::after {
        position: absolute;
        content: '';
        width: 0;
        height: 0;
        right: 0;
        top: 5px;
        clear: both;
        border-top: 4px solid transparent;
        border-bottom: 4px solid transparent;
        border-left: 4px solid ${({ theme }) => theme.colors.red300};

        @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
            top: 7px;
        }
    }
`;
